import { useRef, useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { tracking } from '@iwoca/frontend-tracking-library';
import { useQueryClient } from '@tanstack/react-query';

import type { TEmailValidationBranch } from '../../AnonymousSplitTest/useEmailValidationTest';
import {
  getEmailValidationQueryOptions,
  useGetEmailValidationQuery,
} from '../../api/lendingApi/lendingApiQueries';

export const EMAIL_VALIDATION_TIMEOUT_MS = 3000;

export const INVALID_EMAIL_MESSAGE =
  'We can’t verify this email address. Check for typos and try again, or give us a call.';

export const useEmailValidation = ({
  enabled,
  isBlocking,
  branch,
}: {
  enabled: boolean;
  isBlocking: boolean;
  branch: TEmailValidationBranch;
}) => {
  const queryClient = useQueryClient();
  const [checkedEmail, setCheckedEmail] = useState<string>();
  const trackedEmails = useRef(new Set<string>());
  const { emailValidation } = useGetEmailValidationQuery(
    enabled ? checkedEmail : undefined,
  );

  const checkEmail = async (email: string) => {
    const result = await queryClient.ensureQueryData(
      getEmailValidationQueryOptions(email),
    );

    if (branch && !trackedEmails.current.has(email)) {
      trackedEmails.current.add(email);
      tracking.ampli.emailValidationChecked({
        valid: result.valid,
        has_mx_or_a_record: result.has_mx_or_a_record ?? undefined,
        has_known_bounces: result.has_known_bounces ?? undefined,
        email_validation_split_test: branch,
      });
    }

    return result;
  };

  const checkEmailValidityOnBlur = async ({
    email,
    validateEmail,
  }: {
    email: string;
    validateEmail: () => Promise<boolean>;
  }) => {
    if (!enabled || !email) return;

    if (!(await validateEmail())) return;

    setCheckedEmail(email);
    checkEmail(email).catch(() => {});
  };

  const checkEmailValidityOnSubmit = async (email: string) => {
    if (!enabled) return true;

    setCheckedEmail(email);
    const check = checkEmail(email);

    if (!isBlocking) {
      check.catch(() => {});
      return true;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<boolean>((resolve) => {
      timeoutId = setTimeout(() => {
        datadogLogs.logger.warn('Email validation timed out on submit');
        resolve(true);
      }, EMAIL_VALIDATION_TIMEOUT_MS);
    });

    try {
      return await Promise.race([check.then(({ valid }) => valid), timeout]);
    } catch {
      return true;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const isShowingInvalidEmail = (email: string) =>
    isBlocking && emailValidation?.valid === false && checkedEmail === email;

  return {
    checkEmailValidityOnBlur,
    checkEmailValidityOnSubmit,
    isShowingInvalidEmail,
  };
};
